import { Divider, Grid } from '@material-ui/core';
import { perfilOptions, grupoAcessoOptions, Option } from 'common/genericOptions';
import {
  Perfil,
  ParticipanteResponse,
  UsuarioModelResponse,
  RegistroStatus,
  GrupoAcesso,
} from 'common/genericTypes';
import { Button, LgSpace, AutoComplete, MdSpace, SmSpace } from 'm2m.components';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import { addAlert } from 'store/modules/snackBar';
import { GenericDrawer } from 'components/genericDrawer';
import _ from 'lodash';
import { UsuarioListItem } from '../usuarioListItem';
import { CriarUsuario } from '../criarUsuario';
import { EditarUsuario } from '../editarUsuario';
import ParticipanteAutoComplete from 'components/participante/participanteAutoComplete';
import ReenviarConviteDrawer from '../reenviarConviteDrawer';

export const Usuarios: React.FC = () => {
  const dispatch = useDispatch();
  const isUsuarioLeitura = useSelector((state: RootState) => state.auth.usuario.participante.perfil === Perfil.Leitura);
  const getUsuariosApi = useSelector((state: RootState) => state.usuarioApi.getAllUsuarios);
  const deleteUsuario = useSelector((state: RootState) => state.usuarioApi.deleteUsuario);
  const getParticipantesApi = useSelector((state: RootState) => state.participanteApi.getParticipantesResume);
  const patchDesbloquearUsuario = useSelector((state: RootState) => state.usuarioApi.patchDesbloquearUsuario);
  const isUsuarioGestaoAcessos = useSelector(
    (state: RootState) => state.auth.usuario.grupoAcesso === GrupoAcesso.GestaoAcessos
  );

  const [openEditarUsuarioDrawer, setOpenEditarUsuarioDrawer] = useState(false);
  const [openCriarUsuarioDrawer, setOpenCriarUsuarioDrawer] = useState(false);
  const [openReenviarConviteDrawer, setOpenReenviarConviteDrawer] = useState(false);
  const [openConfirmacaoDrawer, setOpenConfirmacaoDrawer] = useState(false);
  const [usuarios, setUsuarios] = useState<UsuarioModelResponse[]>([]);
  const [participantes, setParticipantes] = useState<ParticipanteResponse[]>([]);
  const [usuarioSelected, setUsuarioSelected] = useState<UsuarioModelResponse>();
  const [perfisSelected, setPerfisSelected] = useState<Array<Option>>([]);
  const [participanteSelected, setParticipanteSelected] = useState<ParticipanteResponse | null>(null);
  const [grupoAcessosSelected, setGrupoAcessosSelected] = useState<Array<Option>>([]);

  const getUsuarios = useCallback(async () => {
    try {
      const result = await getUsuariosApi(dispatch, true);
      setUsuarios(_.orderBy(result, ['dataDeCriacao'], ['desc']));
    } catch (error) { }
  }, []);

  const getParticipantes = useCallback(async () => {
    try {
      const result = await getParticipantesApi(dispatch, { status: RegistroStatus.Aprovado }, true);
      setParticipantes(result);
    } catch (error) { }
  }, []);

  useEffect(() => {
    getUsuarios();
    getParticipantes();
  }, []);

  const handleDesbloquearUsuario = async (usuario: UsuarioModelResponse) => {
    try {
      await patchDesbloquearUsuario(dispatch, usuario.id, true);
      dispatch(
        addAlert({
          message: 'Usuário desbloqueado com sucesso!',
          severity: 'success',
        })
      );
      await getUsuarios();
    } catch (error) {
      dispatch(
        addAlert({
          message: 'Ocorreu um erro ao desbloquear o usuário!',
          severity: 'error',
        })
      );
    }
  };

  const handleOpenEditarUsuarioDrawer = (usuarioToEdit?: UsuarioModelResponse) => {
    setOpenEditarUsuarioDrawer(true);
    setUsuarioSelected(usuarioToEdit);
  };

  const handleCloseEditarUsuarioDrawer = () => {
    setOpenEditarUsuarioDrawer(false);
    setUsuarioSelected(undefined);
  };

  const handleCloseCriarUsuarioDrawer = () => {
    setOpenCriarUsuarioDrawer(false);
  };

  const handleCloseReenviarConviteDrawer = () => {
    setOpenReenviarConviteDrawer(false);
  };

  const handleConfirmarAcao = async () => {
    try {
      await deleteUsuario(dispatch, usuarioSelected!.id, true);
      const newUsuarios = usuarios.filter((u) => u.id !== usuarioSelected!.id);
      setUsuarios(newUsuarios);
      handleCloseConfirmacaoDrawer();
    } catch (error) { }
  };

  const handleOpenConfirmacaoDrawer = (usuario: UsuarioModelResponse) => {
    setUsuarioSelected(usuario);
    setOpenConfirmacaoDrawer(true);
  };

  const handleCloseConfirmacaoDrawer = () => {
    setOpenConfirmacaoDrawer(false);
    setUsuarioSelected(undefined);
  };

  const filterUsuarios = () => {
    let result = usuarios;

    if (participanteSelected) {
      result = result.filter(u => u.participanteFuncoes.some(pf => pf.participanteId === participanteSelected.id));
    }

    if (perfisSelected.length)
      result = result.filter((u) =>
        u.participanteFuncoes.some((pf) => perfisSelected.some((ps) => ps.value === pf.perfil))
      );

    if (grupoAcessosSelected.length)
      result = result.filter((u) => grupoAcessosSelected.some((g) => g.value === u.grupoAcesso));

    return result;
  };

  const filteredUsuarios = filterUsuarios();

  const ConfirmarLiquidacaoDrawer = (
    <GenericDrawer
      opened={openConfirmacaoDrawer}
      onClose={handleCloseConfirmacaoDrawer}
      title={`Excluir Usuário`}
      textContents={[
        {
          text: `Tem certeza que deseja excluir o usuário ${usuarioSelected?.nome ?? ''}?`,
        },
      ]}
      buttons={[
        {
          buttonText: `Confirmar`,
          buttonAction: handleConfirmarAcao,
          buttonColor: 'error',
        },
        {
          buttonText: 'Cancelar',
          buttonAction: handleCloseConfirmacaoDrawer,
        },
      ]}
    />
  );

  return (
    <>
      <SmSpace />
      {!isUsuarioLeitura && (
        <Grid container justifyContent="flex-end" style={{ marginBottom: 16 }}>
          <Button variant="contained" color="primary" onClick={() => setOpenCriarUsuarioDrawer(true)}>
            {'Novo Usuário'}
          </Button>
        </Grid>
      )}
      <div>
        <Divider />
        <MdSpace />
        <Grid container spacing={2}>
          {isUsuarioGestaoAcessos && (
            <>
              <Grid item xs={3}>
                <ParticipanteAutoComplete
                  label={`Participante`}
                  participantes={participantes}
                  participante={participanteSelected}
                  onChange={(value) => setParticipanteSelected(value)}
                />
              </Grid>
              <Grid item xs={3}>
                <AutoComplete
                  multiple
                  options={grupoAcessoOptions}
                  getOptionLabel={(option: Option) => option.description}
                  value={grupoAcessosSelected}
                  onChange={(values: Array<Option>) => setGrupoAcessosSelected(values)}
                  filterSelectedOptions
                  label={'Grupo de Acesso'}
                  renderOption={(option: Option) => option.description}
                />
              </Grid>
            </>
          )}
          <Grid item xs={3}>
            <AutoComplete
              multiple
              options={perfilOptions}
              getOptionLabel={(option: Option) => option.description}
              value={perfisSelected}
              onChange={(values: Array<Option>) => setPerfisSelected(values)}
              filterSelectedOptions
              label={'Perfil'}
              renderOption={(option: Option) => option.description}
            />
          </Grid>
          <Grid item xs={3}>
            {!isUsuarioLeitura && isUsuarioGestaoAcessos && (
              <Button variant="contained" color="primary" onClick={() => setOpenReenviarConviteDrawer(true)}>
                {'Reenviar Convite'}
              </Button>
            )}
          </Grid>
        </Grid>
        <MdSpace />
        <Divider />
      </div>
      <LgSpace />
      <Grid container spacing={3}>
        {filteredUsuarios.map((usuario) => (
          <Grid key={usuario.id} item xs={12}>
            <UsuarioListItem
              usuario={usuario}
              onEdit={(value) => handleOpenEditarUsuarioDrawer(value)}
              onDelete={(value) => handleOpenConfirmacaoDrawer(value)}
              onDesbloquear={(value) => handleDesbloquearUsuario(value)}
            />
          </Grid>
        ))}
      </Grid>
      <LgSpace />
      {openCriarUsuarioDrawer && <CriarUsuario
        open={openCriarUsuarioDrawer}
        participantes={participantes}
        onClose={handleCloseCriarUsuarioDrawer}
      />}
      {openReenviarConviteDrawer && 
      <ReenviarConviteDrawer
        open={openReenviarConviteDrawer}
        onClose={handleCloseReenviarConviteDrawer}
      />}
      {usuarioSelected && <EditarUsuario
        open={openEditarUsuarioDrawer}
        participantes={participantes}
        usuario={usuarioSelected}
        onClose={handleCloseEditarUsuarioDrawer}
      />}
      {ConfirmarLiquidacaoDrawer}
    </>
  );
};

export default Usuarios;
